import { observer } from "mobx-react";
import exchangeInterfaceStore from "../../../stores/ExchangeInterfaceStore";
import {
  BASE_MULTIPLIER,
  PAYMENT_METHODS,
  PRESALE_ID,
  STATUS,
} from "../../../utils/constants";
import { toast } from "react-toastify";
import { useLatestBNBPrice } from "../../../utils/hooks/presale-manager/read/useLatestBNBPrice";
import { usePresale } from "../../../utils/hooks/presale-manager/read/usePresale";
import { useApproveUSDT } from "../../../utils/hooks/usdt-manager/write/useApproveUSDT";
import { useBuyWithUSDT } from "../../../utils/hooks/presale-manager/write/useBuyWithUSDT";
import { useBuyWithBNB } from "../../../utils/hooks/presale-manager/write/useBuyWithBNB";
import { useWaitForTransactionReceipt } from "wagmi";
import { useEffect } from "react";
import { useAllowance } from "../../../utils/hooks/usdt-manager/read/useAllowance";
import Button from "../Button";
import { resetInputValues } from "../../../utils/helpers";
import { useUSDTBalance } from "../../../utils/hooks/usdt-manager/read/useUSDTBalance";
import { useBNBBalance } from "../../../utils/hooks/presale-manager/read/useBNBBalance";

const BuyButton = () => {
  const { activePaymentMethod, inputsValues, statuses, setStatus, referrer } =
    exchangeInterfaceStore;

  const { baseCoinsToPay, pepeCoinsToReceive } = inputsValues;

  const presaleData = usePresale(PRESALE_ID)?.data!;
  const bnbPrice = useLatestBNBPrice()?.bnbPrice!;
  const { approveUSDT, approveUSDTHash } = useApproveUSDT()!;

  const buyWithUSDTData = useBuyWithUSDT()!;
  const buyWithUSDT = buyWithUSDTData?.buyWithUSDT!;
  const buyWithUSDTHash = buyWithUSDTData?.buyWithUSDTHash;
  const refetchUSDTBalance = useUSDTBalance()?.refetchUSDTBalance!;

  const buyWithBNBData = useBuyWithBNB()!;
  const buyWithBNB = buyWithBNBData?.buyWithBNB!;
  const buyWithBNBHash = buyWithBNBData?.buyWithBNBHash!;
  const refetchBNBBalance = useBNBBalance()?.refetchBNBBalance!;

  const uAllowance = useAllowance();
  const refetchAllowance = uAllowance?.refetchUSDTAllowance!;

  const buttonsClasses = {
    green: `bg-gradient-to-r from-lime-400 via-green-400 to-green-500 hover:scale-105  hover:shadow-lg hover:shadow-green-500  duration-300 hover:from-green-400 hover:to-green-500`,
    blue: `bg-gradient-to-r from-sky-400 via-blue-400 to-blue-500 `,
    red: `bg-gradient-to-r from-rose-400 via-red-400 to-red-500 `,
  };

  const handler: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.preventDefault();

    if (typeof baseCoinsToPay !== "number") return;
    if (typeof pepeCoinsToReceive !== "number") return;

    if (statuses.PRESALE_ENDED) return toast.error("Presale ended");

    setStatus(STATUS.LOADING, true);

    if (activePaymentMethod === PAYMENT_METHODS.USDT) {
      if (statuses.NOT_ENOUGH_ALLOWANCE) {
        const usdtToApprove = BigInt(100000000 * BASE_MULTIPLIER);
        await approveUSDT(usdtToApprove);
        setStatus(STATUS.LOADING, false);
        return
      }

      const tokensToBuy = BigInt(Math.floor(pepeCoinsToReceive));

      await buyWithUSDT({
        presaleId: PRESALE_ID,
        amountOfTokensToBuy: tokensToBuy,
        referrer: referrer as `0x${string}`
      });
    }

    if (activePaymentMethod === PAYMENT_METHODS.BNB) {
      const tokensToBuy = BigInt(Math.floor(pepeCoinsToReceive));
      const bnbToSpend =
        (Math.floor(pepeCoinsToReceive) * presaleData.price) / bnbPrice +
        0.0001;
      await buyWithBNB({
        presaleId: PRESALE_ID,
        amountOfTokensToBuy: tokensToBuy,
        bnbToSpend,
        referrer: referrer as `0x${string}`
      });
    }

    resetInputValues();
    setStatus(STATUS.LOADING, false);
    return;
  };

  const { isSuccess: isUSDTApprovalConfirmed } = useWaitForTransactionReceipt({
    hash: approveUSDTHash,
  });

  const { isSuccess: isBuyWithUSDTConfirmed } = useWaitForTransactionReceipt({
    hash: buyWithUSDTHash,
  });

  const { isSuccess: isBuyWithBNBConfirmed } = useWaitForTransactionReceipt({
    hash: buyWithBNBHash,
  });

  useEffect(() => {
    if (isUSDTApprovalConfirmed && refetchAllowance) {
      toast.success("USDT approved succesfully");
      refetchAllowance();
    }
    if (isBuyWithUSDTConfirmed && refetchUSDTBalance) {
      toast.success("Tokens bought succesfully");
      refetchUSDTBalance();
    }
    if (isBuyWithBNBConfirmed && refetchBNBBalance) {
      toast.success("Tokens bought succesfully");
      refetchBNBBalance();
    }
  }, [
    isUSDTApprovalConfirmed,
    isBuyWithUSDTConfirmed,
    isBuyWithBNBConfirmed,
    refetchUSDTBalance,
    refetchAllowance,
    refetchBNBBalance,
    approveUSDTHash,
    buyWithUSDTHash,
    buyWithBNBHash
  ]);

  if (statuses.WRONG_NETWORK) {
    return (
      <Button
        text="Wrong network"
        disabled={true}
        classes={buttonsClasses.red}
      />
    );
  }

  if (statuses.WALLET_DISCONNECTED) {
    return (
      <Button
        text="Not connected"
        disabled={true}
        classes={buttonsClasses.red}
      />
    );
  }

  if(statuses.BELOW_MINIMUM){
    return (
      <Button
        text="Amount below minimum"
        disabled={true}
        classes={buttonsClasses.red}
      />
    );
  }

  if (statuses.NOT_ENOUGH_BALANCE) {
    return (
      <Button
        text="No balance"
        disabled={true}
        classes={buttonsClasses.red}
      />
    );
  }

  if (statuses.NOT_ENOUGH_FEES) {
    return (
      <Button
        text="Not enough for fees"
        disabled={true}
        classes={buttonsClasses.red}
      />
    );
  }

 


  if (statuses.NOT_ENOUGH_ALLOWANCE) {
    return (
      <Button
        text="Approve USDT"
        disabled={false}
        classes={buttonsClasses.blue}
        onClick={handler}
      />
    );
  }

  if (statuses.LOADING) {
    return (
      <Button
        text="Loading..."
        disabled={true}
        classes={buttonsClasses.blue}
      />
    );
  }

  if (statuses.PRESALE_ENDED) {
    return (
      <Button
        text="Presale ended"
        disabled={true}
        classes={buttonsClasses.red}
      />
    );
  }

  if(statuses.NOT_ENOUGH_TOKENS){
    return (
      <Button
        text="Invalid amount of tokens"
        disabled={true}
        classes={buttonsClasses.red}
      />
    );
  }

  
  return (
    <Button
      text="Buy now"
      disabled={false}
      classes={buttonsClasses.green}
      onClick={handler}
    />
  );
};

export default observer(BuyButton);
